import type {ReactElement} from 'react';
import React, {memo, useRef, useLayoutEffect, useEffect, useState} from 'react';
import type {DragMoveEvent, ItemDefinition, Range, RowDefinition} from '@Libraries/timeline/types';
import {TimelineContext} from '@Libraries/timeline';
import {getPosterDuration} from '@Components/poster-editor/poster-editor-reducer';
import {noop} from '@Utils/general.util';
import type {DragEndEvent} from '@dnd-kit/core/dist/types';
import {
  updateItemDragDataState,
  updateItemDraggingState,
  updatePanningStatus,
  updateTimelineWidthPerSecond,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {
  generateTimelineItems,
  generateTranscriptItemRows,
  isAValidAudioItem,
  isAValidTranscriptSubtitleItem,
  onAudioItemResizeEnd,
  onTranscriptItemResizeEnd,
  TimelineItemTypeId,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import {TIMELINE_MAX_WIDTH_PER_SECOND} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {restrictToHorizontalAxis} from '@dnd-kit/modifiers';
import {closestCorners, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {useAppDispatch, useAppSelector} from '@/hooks';
import BaseTimelineContent from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline-content/base-timeline-content';
import {useGetNumberOfAudios} from '@Hooks/poster-editor/useGetNumberOfAudios';

function BaseTimeline(): ReactElement<any> | null {
  const posterDuration = useAppSelector(getPosterDuration);
  const audioData = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips;
  });
  const numOfAudios = useGetNumberOfAudios();
  const [items, setItems] = useState<ItemDefinition[]>(generateTimelineItems());
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const isItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });
  const isItemDragging = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isItemDragging;
  });
  const timer = useRef<number | undefined>(undefined);
  const dispatch = useAppDispatch();
  const webBottomBarRef = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_WEB_BOTTOM_BAR];
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
        delay: 300,
      },
    })
  );

  useLayoutEffect(() => {
    if (isEditorMobileVariant()) {
      return noop;
    }
    if (!webBottomBarRef) {
      return noop;
    }
    const element = webBottomBarRef.get(0);
    if (!element) {
      return noop;
    }
    const wheelHandler = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;

      event.preventDefault();
      event.stopPropagation();

      updatePanningState(true);
      const scale = widthPerSecond > 80 ? 1 : (2 * widthPerSecond) / TIMELINE_MAX_WIDTH_PER_SECOND;
      updateWidthPerSecond(widthPerSecond + event.deltaY * -1 * scale);

      if (timer) {
        clearTimeout(timer.current);
      }

      timer.current = window.setTimeout(() => {
        updatePanningState(false);
      }, 500);
    };

    element.addEventListener('wheel', wheelHandler);

    return () => {
      element.removeEventListener('wheel', wheelHandler);
    };
  }, [webBottomBarRef, widthPerSecond]);

  useEffect((): void => {
    setItems(generateTimelineItems());
  }, [audioData, posterDuration, numOfAudios, isItemDragging]);

  if (!posterDuration) {
    return null;
  }

  const getRange = (): Range => {
    return {
      start: 0,
      end: posterDuration,
    };
  };

  const onDragEnded = async (event: DragEndEvent): Promise<void> => {
    const itemId = event.active.id as string;
    const activeItemId = itemId.split('_')[0];
    const dragElement = event.active.data.current;
    const updatedRange = dragElement?.getSpanFromDragEvent?.(event) as Range;

    if (!activeItemId || !dragElement || !updatedRange) {
      return;
    }

    if (isAValidAudioItem(activeItemId)) {
      await onAudioItemResizeEnd(updatedRange, activeItemId);
    }
    if (isAValidTranscriptSubtitleItem(itemId.split('_')[1])) {
      await onTranscriptItemResizeEnd(updatedRange, activeItemId);
    }

    dispatch(updateItemDraggingState(false));
    dispatch(updateItemDragDataState(null));
  };

  const getRows = (): RowDefinition[] => {
    const rows = [
      {
        id: TimelineItemTypeId.Design,
      },
    ];

    if (audioData?.audioItemsHashMap && Object.keys(audioData.audioItemsHashMap).length !== 0) {
      rows.push({
        id: TimelineItemTypeId.Audio,
      });
    }

    return [...rows, ...generateTranscriptItemRows()];
  };

  const updatePanningState = (status: boolean): void => {
    dispatch(updatePanningStatus(status));
  };

  const updateWidthPerSecond = (newWidth: number): void => {
    dispatch(updateTimelineWidthPerSecond(newWidth));
  };

  const onDragStart = (): void => {
    dispatch(updateItemDraggingState(true));
  };

  const onDragMove = (event: DragMoveEvent): void => {
    const activeItemId = event.active.id as string;
    const dragElement = event.active.data.current;
    const updatedRange = dragElement?.getSpanFromDragEvent?.(event)!;
    if (!updatedRange) {
      return;
    }

    dispatch(
      updateItemDragDataState({
        id: isAValidAudioItem(activeItemId) ? activeItemId : activeItemId.split('_')[0],
        transcriptId: activeItemId.split('_')[1],
        dragRange: updatedRange,
        type: isAValidAudioItem(activeItemId) ? TimelineItemTypeId.Audio : TimelineItemTypeId.Transcript,
      })
    );
  };

  return (
    <TimelineContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      autoScroll={!isItemResizing}
      resizeHandleWidth={30}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onResizeEnd={noop}
      onRangeChanged={noop}
      range={getRange()}
      onDragEnd={(e): void => {
        void onDragEnded(e);
      }}
      collisionDetection={closestCorners}
    >
      <BaseTimelineContent id="timeline-content" items={items} rows={getRows()} />
    </TimelineContext>
  );
}

export default memo(BaseTimeline);
